import cn from "classnames";
import { FormTemplate } from "components/modal/templates";
import { Datetime } from "components/shared/form";
import { formatDateTimezone, getPointTime, isBidPoint, isStartBidPoint, START_POINT_LETTER } from "helpers/bidpoint";
import { dateTimeToView, momentDateInOffset, plainDate, timeOffset } from "helpers/date";
import { bidPointTypesEnum, mapObjectTypesEnum } from "helpers/enums";
import { getInitialStateFromConfig, serializeReq, validateForm } from "helpers/form";
import { formatNumber, pointLetter } from "helpers/system";
import { pastDate, requiredField } from "helpers/validators";
import cloneDeep from "lodash/cloneDeep";
import moment from "moment";
import React, { Fragment } from "react";
import { alert } from "services/alert";
import { forceComplete, updateOdometerMileage } from "sources/books/bids";

const config = {
    bidId: {},
    bidPoints: {
        config: {
            enteredAtByLogist: {
                label: "Дата прибытия на точку",
                validators: [requiredField, pastDate],
                required: true,
                inputProps: {
                    name: "enteredAtByLogist",
                    type: "datetime",
                },
            },
            loadUnloadedAtByLogist: {
                label: "Дата загрузки/выгрузки",
                validators: [requiredField, pastDate],
                required: true,
                inputProps: {
                    name: "loadUnloadedAtByLogist",
                    type: "datetime",
                },
            },
        },
    },
    itemChangePoints: {
        config: {
            manualStartAt: {
                label: "Дата прибытия на точку",
                validators: [requiredField, pastDate],
                required: true,
                inputProps: {
                    name: "manualStartAt",
                    type: "datetime",
                },
            },
            manualEndAt: {
                label: "Дата выезда из точки",
                validators: [requiredField, pastDate],
                required: true,
                inputProps: {
                    name: "manualEndAt",
                    type: "datetime",
                },
            },
        },
    },
    reason: {
        label: "Причина завершения заявки",
        inputProps: { name: "reason", maxLength: 255 },
        validators: [requiredField],
        required: true,
    },
    mileage: {
        label: "Фактический пробег",
        inputProps: { name: "mileage", type: "number", min: 0 },
        validators: [requiredField],
        required: true,
    },
    useOdometerMileage: {
        label: "Использовать данные с одометра",
        inputProps: { name: "useOdometerMileage" },
        checkbox: true,
    },
};

let initialState = getInitialStateFromConfig(config);

class ForceComplete extends FormTemplate {
    constructor(props) {
        super(props);
        this.firstLoadPointIndex = -1;
        props.initialState.bidPoints.some((item) => {
            if (isBidPoint(item.geozoneType)) {
                this.firstLoadPointIndex++;
            }
            return [bidPointTypesEnum.LoadPoint, bidPointTypesEnum.UnloadPoint].includes(item.type);
        });
    }

    sendRequest = (stateData) => forceComplete(stateData);

    handleSubmit = (e) => {
        e.preventDefault();
        const { isLoading, ...state } = this.state;
        if (!isLoading) {
            if (this.isValidForm(state)) {
                const action = () => {
                    return this.sendRequest(serializeReq(state)).then((res) => {
                        if (res) {
                            this.props.mergeParams && this.props.mergeParams();
                            this.close(res);
                        } else {
                            this.setState({ isLoading: false, isValid: false });
                        }
                    });
                };
                this.setState({ isLoading: true, isValid: true });
                const planMileage = this.props.bid.planMileage / 1000;
                const diff = Math.abs((state.mileage - planMileage) / planMileage);

                if (diff > 0.2) {
                    return new Promise((resolve) => {
                        this.props.add({
                            name: "confirm",
                            title: "Подтвердите пробег",
                            text: `Введенный пробег ${state.mileage} отличается от планового ${formatNumber(
                                planMileage,
                                {
                                    maximumFractionDigits: 0,
                                },
                            )}, вы уверены что указали пробег правильно?`,
                            action: () => action().then(resolve),
                            onCloseAction: () => {
                                this.setState({ isLoading: false });
                                resolve();
                            },
                            buttons: { confirm: "Да", cancel: "Нет" },
                        });
                    });
                } else {
                    action();
                }
            }
            this.setState({ isValid: false });
        }
    };

    isValidForm = (state) => {
        const { config } = this.props;
        const formatted = cloneDeep(state);
        formatted.bidPoints.forEach((point) => {
            point.enteredAtByLogist = `${point.enteredAtByLogist}${point.timeOffset}`;
            point.loadUnloadedAtByLogist = `${point.loadUnloadedAtByLogist}${point.timeOffset}`;
        });
        return validateForm(config, serializeReq(formatted));
    };

    setValue = ({ name, value }) => {
        const isValid = this.isValidForm({ ...this.state, [name]: value });
        this.setState({ [name]: value, ...(isValid ? { isValid: true } : {}) });
        if (name === "useOdometerMileage" && value) {
            setTimeout(() => {
                this.setState({ mileage: this.state.odometerMileage });
            }, 0);
        }
    };

    renderBidPoint = (point, index) => {
        const isServicePoint = point.type === bidPointTypesEnum.ServicePoint;
        const pointIconClassName = cn("book-form__section-head--point-icon", {
            "icon-uEA97-wrench": isServicePoint,
        });
        const setPlanDates = () => {
            const { bidPoints } = this.state;
            const newBidPoints = bidPoints.map((item) =>
                item.id === point.id
                    ? {
                          ...item,
                          enteredAtByLogist: plainDate(point.planEnterDateOffset),
                          loadUnloadedAtByLogist: plainDate(point.planLeaveDateOffset),
                      }
                    : item,
            );

            this.setState({ bidPoints: newBidPoints });
        };
        return (
            <Fragment key={point.id}>
                <div className="row">
                    <label className="col-sm-4 col-md-4 col-form-label col-form-label-sm text-md-right text-sm-left">
                        <div className="d-inline-block position-relative">
                            <div className={pointIconClassName}>{isServicePoint ? null : point.letter}</div>
                        </div>
                        Адрес
                    </label>
                    <div className="col-sm-8 col-md-8 col-form-label-sm align-self-center">
                        {point.geozone.address}
                        &nbsp;
                        <span className="text-muted">{formatDateTimezone(point)}</span>
                    </div>
                </div>
                <div className="row">
                    <div className="col-sm-8 col-md-8 col-form-label-sm offset-sm-4 offset-md-4">
                        <div className="btn btn-xs btn-secondary" onClick={setPlanDates}>
                            Установить плановые даты
                        </div>
                    </div>
                </div>
                {this.renderDateField(config.bidPoints.config.enteredAtByLogist, point.id, "bidPoints", index)}
                {this.renderDateField(config.bidPoints.config.loadUnloadedAtByLogist, point.id, "bidPoints", index)}
                <hr />
            </Fragment>
        );
    };

    renderItemChangePoint = (point, i) => {
        const setPlanDates = () => {
            const { itemChangePoints } = this.state;
            const newPoints = itemChangePoints.map((item) =>
                item.id === point.id
                    ? {
                          ...item,
                          manualStartAt: plainDate(point.planEnterDateOffset),
                          manualEndAt: plainDate(moment(point.planEnterDateOffset).add(30, "m")),
                      }
                    : item,
            );

            this.setState({ itemChangePoints: newPoints });
        };
        return (
            <Fragment key={point.id}>
                <div className="row">
                    <label className="col-sm-4 col-md-4 col-form-label col-form-label-sm text-md-right text-sm-left">
                        <div className="d-inline-block position-relative">
                            <div className="book-form__section-head--point-icon icon-uEAB3-arrows-rotate" />
                        </div>
                        Адрес
                    </label>
                    <div className="col-sm-8 col-md-8 col-form-label-sm align-self-center">
                        {point.mapObject.address}
                        &nbsp;
                        <span className="text-muted">{formatDateTimezone(point)}</span>
                    </div>
                </div>
                <div className="row">
                    <div className="col-sm-8 col-md-8 col-form-label-sm offset-sm-4 offset-md-4">
                        <div className="btn btn-xs btn-secondary" onClick={setPlanDates}>
                            Установить плановые даты
                        </div>
                    </div>
                </div>
                {this.renderDateField(config.itemChangePoints.config.manualStartAt, point.id, "itemChangePoints", i)}
                {this.renderDateField(config.itemChangePoints.config.manualEndAt, point.id, "itemChangePoints", i)}
                <hr />
            </Fragment>
        );
    };

    renderDateField = (props, pointId, key, index) => {
        const point = this.state[key].find((item) => item.id === pointId);
        const bWrapper = cn("row", { "mb-2": !point.errors[props.inputProps.name] });
        const setDateValue = ({ name, value }) => {
            const oldValue = this.state[key];
            const newValue = oldValue.map((item) =>
                item.id === pointId
                    ? {
                          ...item,
                          [name]: value ? plainDate(value) : "",
                      }
                    : item,
            );
            this.setState({ [key]: newValue });
        };
        return (
            <Datetime
                inPortal
                bWrapper={bWrapper}
                key={`${key}_${pointId}_${props.inputProps.name}`}
                wrapper="col-sm-8 col-md-4"
                labelWrapper="col-sm-4 col-md-4 col-form-label col-form-label-sm text-md-right text-sm-left"
                label={props.label}
                index={index}
                libOptions={{
                    name: `${props.inputProps.name}_${index}`,
                    autoComplete: "off",
                    maxDate: momentDateInOffset(moment(), point.timeOffset),
                }}
                timeOffset={point.timeOffset}
                onChange={(prop) => setDateValue(prop)}
                value={point[props.inputProps.name]}
                {...props}
            />
        );
    };

    refreshOdometerMileage = () => {
        const odometerPeriod = this.odometerPeriod();
        if (!odometerPeriod.start || !odometerPeriod.end) {
            return alert({ type: "error", message: "Введите даты точек заявки", title: "Ошибка" });
        }
        this.setState({ isLoading: true });
        const startedAt = `${odometerPeriod.start}${odometerPeriod.startOffset}`;
        const endedAt = `${odometerPeriod.end}${odometerPeriod.endOffset}`;
        updateOdometerMileage({ id: this.props.bid.id, startedAt, endedAt }).then((data) =>
            this.setState({ odometerMileage: data || undefined, isLoading: false }),
        );
    };

    odometerPeriod = () => {
        const { bidPoints } = this.state;
        const firstBidPoint = bidPoints.find((item) =>
            [bidPointTypesEnum.LoadPoint, bidPointTypesEnum.UnloadPoint].includes(item.type),
        );
        const lastBidPoint = bidPoints[bidPoints.length - 1];
        return {
            start: firstBidPoint.loadUnloadedAtByLogist,
            startOffset: firstBidPoint.timeOffset,
            end: lastBidPoint.enteredAtByLogist,
            endOffset: lastBidPoint.timeOffset,
        };
    };

    mileageInputBottomText = () => {
        const odometerPeriod = this.odometerPeriod();
        const startTime = odometerPeriod.start
            ? `${dateTimeToView(odometerPeriod.start)} (${odometerPeriod.startOffset})`
            : "-";
        const endTime = odometerPeriod.end
            ? `${dateTimeToView(odometerPeriod.end)} (${odometerPeriod.endOffset})`
            : "-";
        return (
            <div className="field-wrap__bottom-text pt-1">
                {this.state.odometerMileage && (
                    <>
                        <span className="pr-2">
                            <span>Значение фактического пробега по одометру за период</span>&nbsp;
                            {startTime} — {endTime}: <b>{formatNumber(this.state.odometerMileage)}&nbsp;км</b>
                        </span>
                        <br />
                    </>
                )}
                <span className="link font-italic" onClick={this.refreshOdometerMileage}>
                    Обновить данные по пробегу
                </span>
            </div>
        );
    };

    renderBodyInner = () => {
        let index = -1;
        const { pointsOrderedByRoute } = this.props;
        const { reason, mileage, useOdometerMileage } = this.props.config;
        const classProps = {
            fieldWrapper: "col-sm-8 col-md-4",
            labelWrapper: "col-sm-4 col-md-4 col-form-label col-form-label-sm text-md-right text-sm-left",
            errorWrapper: "field-error",
        };
        return (
            <>
                {pointsOrderedByRoute.map((item, i) => {
                    if (isBidPoint(item.type)) {
                        index++;
                    }
                    const letter = isStartBidPoint(item.type)
                        ? START_POINT_LETTER
                        : pointLetter(index, this.firstLoadPointIndex);
                    if (
                        isBidPoint(item.type) ||
                        [
                            mapObjectTypesEnum.StartBidPoint,
                            mapObjectTypesEnum.ServicePoint,
                            mapObjectTypesEnum.RouteSupportPoint,
                        ].includes(item.type)
                    ) {
                        return this.renderBidPoint({ ...item.data, letter }, i);
                    } else if (item.type === mapObjectTypesEnum.ItemChangePoint) {
                        return this.renderItemChangePoint(item.data, i);
                    } else {
                        return null;
                    }
                })}
                {this.renderField({
                    ...mileage,
                    ...classProps,
                    inputBottomText: this.mileageInputBottomText(),
                    disabled: this.state.useOdometerMileage,
                })}
                {this.renderCheckbox({ ...useOdometerMileage, ...classProps })}
                {this.renderField({ ...reason, ...classProps })}
            </>
        );
    };
}

const ConfiguredModal = (props) => {
    const newState = props.pointsOrderedByRoute.reduce(
        (acc, item) => {
            if (item.type === mapObjectTypesEnum.ItemChangePoint) {
                const { manualStartAt, autoEnteredAt, manualEndAt, autoLeftAt } = item.data;
                return {
                    ...acc,
                    itemChangePoints: [
                        ...acc.itemChangePoints,
                        {
                            id: item.data.id,
                            manualStartAt: getPointTime(manualStartAt, autoEnteredAt),
                            manualEndAt: getPointTime(manualEndAt, autoLeftAt),
                            timeOffset: timeOffset(item.data.planEnterDateOffset),
                            errors: {},
                        },
                    ],
                };
            } else if (
                isBidPoint(item.type) ||
                [
                    mapObjectTypesEnum.StartBidPoint,
                    mapObjectTypesEnum.ServicePoint,
                    mapObjectTypesEnum.RouteSupportPoint,
                ].includes(item.type)
            ) {
                const {
                    id,
                    planEnterDateOffset,
                    autoEnteredAt,
                    autoLeavedAt,
                    enteredAt,
                    loadUnloadedAt,
                    enteredAtByLogist,
                    loadUnloadedAtByLogist,
                    type,
                    geozone,
                } = item.data;
                return {
                    ...acc,
                    bidPoints: [
                        ...acc.bidPoints,
                        {
                            id,
                            enteredAtByLogist: getPointTime(enteredAtByLogist, autoEnteredAt, enteredAt),
                            loadUnloadedAtByLogist: getPointTime(loadUnloadedAtByLogist, autoLeavedAt, loadUnloadedAt),
                            timeOffset: timeOffset(planEnterDateOffset),
                            type,
                            geozoneType: geozone?.type,
                            errors: {},
                        },
                    ],
                };
            } else {
                return acc;
            }
        },
        { bidPoints: [], itemChangePoints: [] },
    );
    initialState = { ...initialState, ...newState, bidId: props.bid.id, odometerMileage: props.bid.odometerMileage };
    return <ForceComplete {...props} config={config} initialState={initialState} />;
};

export default ConfiguredModal;
